import dayjs from "dayjs";

import { AutoCompleteOption } from "../components/form/APAutoComplete";
import { APSliderRangeFilter } from "../components/form/APSlider";
import { SearchFilter } from "../types/Search";

// Slider Filter -> URL Search Param Value
export const APSliderRangeFilterToSearchParam = (
  filter: APSliderRangeFilter
): string | null => {
  switch (filter.type) {
    case "any":
      return null;
    case "between":
      return `between:${filter.min},${filter.max}`;
    case "lte":
      return `lte:${filter.value}`;
    case "gte":
      return `gte:${filter.value}`;
    case "eq":
      return `eq:${filter.value}`;
  }
};

// URL Search Param Value -> Slider Filter
export const SearchParamToAPSliderRangeFilter = (
  param: string
): APSliderRangeFilter | null => {
  const [type, value] = param.split(":");

  switch (type) {
    case "between":
      return {
        type: "between",
        min: parseInt(value.split(",")[0]),
        max: parseInt(value.split(",")[1]),
      };
    case "lte":
      return { type: "lte", value: parseInt(value) };
    case "gte":
      return { type: "gte", value: parseInt(value) };
    case "eq":
      return { type: "eq", value: parseInt(value) };
    default:
      return null;
  }
};

// URL Search Param Value -> Slider Filter
export const SearchParamToStringFilter = (param: string): string | null => {
  const [, value] = param.split(":");

  return value;
};

// Auto Complete Options -> URL Search Param Value
export const AutoCompleteOptionsToSearchParam = (
  options: AutoCompleteOption[]
): string | null => {
  if (options.length === 0) {
    return null;
  }

  if (options.length === 1) {
    return `eq:${options[0].id}`;
  }

  return `in:${options.map((o) => o.id).join(",")}`;
};

// Between Multiple Options -> URL Search Param Value
export const BetweenMultipleOptionsToSearchParam = (
  options: AutoCompleteOption[]
): string | null => {
  return `betweenMultiple:${options
    .map((between) => {
      const [min, max] = between.id.split("to");
      return `${min}to${max ?? ""}`;
    })
    .join(",")}`;
};

// URL Search Param Value -> Auto Complete Options
export const SearchParamToAutoCompleteOptions = (
  param: string,
  allOptions: AutoCompleteOption[]
): AutoCompleteOption[] | null => {
  const [type, value] = param.split(":");

  if (type === "in") {
    return allOptions.filter((o) => value.split(",").includes(o.id));
  } else if (type === "eq") {
    return allOptions.filter((o) => o.id === value);
  } else if (type === "betweenMultiple") {
    const betweens = value.split(",");
    const optionIds = betweens.map((between) => {
      const [min, max] = between.split("to");
      return max ? `${min}to${max}` : min;
    });
    return allOptions.filter((o) => optionIds.includes(o.id));
  }

  return null;
};

export const getSearchFilterFromParam = (
  params: URLSearchParams,
  key: string,
  valueIsString: boolean = false
): SearchFilter | undefined => {
  const param = params.get(key);
  return !param
    ? undefined
    : SearchParamToSearchFilter(param, valueIsString) ?? undefined;
};

// URL Search Param Value -> API Search Filters
export const SearchParamToSearchFilter = (
  param: string,
  valueIsString: boolean = false
): SearchFilter | null => {
  const delimiterIndex = param.indexOf(":");
  const type = param.slice(0, delimiterIndex);
  const value = param.slice(delimiterIndex + 1);

  switch (type) {
    case "in": {
      return { type: "in", values: value.split(",") };
    }
    case "between": {
      const [min, max] = value.split(",");
      if (min && max) {
        return { type: "between", min: parseInt(min), max: parseInt(max) };
      }
      return null;
    }
    case "lte": {
      return { type: "lte", value: valueIsString ? value : parseInt(value) };
    }
    case "gte": {
      return { type: "gte", value: valueIsString ? value : parseInt(value) };
    }
    case "eq": {
      return { type: "eq", value: valueIsString ? value : parseInt(value) };
    }
    case "betweenMultiple": {
      return {
        type: "betweenMultiple",
        values: value.split(",").map((between) => {
          const [min, max] = between.split("to");

          return [parseInt(min), parseInt(max)];
        }),
      };
    }
    default:
      return null;
  }
};

export function addSliderRangeFilterToParams(
  params: URLSearchParams,
  key: string,
  filter: APSliderRangeFilter
): void {
  const param = APSliderRangeFilterToSearchParam(filter);
  if (param) {
    params.set(key, param);
  }
}

export function addAutoCompleteOptionToParams(
  params: URLSearchParams,
  key: string,
  options: AutoCompleteOption[]
): void {
  const param = AutoCompleteOptionsToSearchParam(options);
  if (param) {
    params.set(key, param);
  }
}

export function addBetweenMultipleFilterToParams(
  params: URLSearchParams,
  key: string,
  options: AutoCompleteOption[]
): void {
  const param = BetweenMultipleOptionsToSearchParam(options);
  if (param) {
    params.set(key, param);
  }
}

export function addStringFilterToParams(
  params: URLSearchParams,
  key: string,
  value: string,
  operator: "eq" | "gte" | "lte"
): void {
  params.set(key, `${operator}:${value}`);
}

export function addDateFilterToParams(
  params: URLSearchParams,
  key: string,
  value: string,
  operator: "eq" | "gte" | "lte"
): void {
  const date = dayjs(value).toISOString();

  addStringFilterToParams(params, key, date, operator);
}

// URL Search Params -> Form Input
export function getAutoCompleteOptionsFromParam(
  params: URLSearchParams,
  key: string,
  allOptions: AutoCompleteOption[]
): AutoCompleteOption[] | null {
  const param = params.get(key);
  return !param
    ? null
    : SearchParamToAutoCompleteOptions(param, allOptions) ?? null;
}

export function getAutoCompleteOptionFromParam(
  params: URLSearchParams,
  key: string,
  allOptions: AutoCompleteOption[]
): AutoCompleteOption | null {
  const options = getAutoCompleteOptionsFromParam(params, key, allOptions);
  return options && options.length > 0 ? options[0] : null;
}

export function getSliderRangeFilterFromParam(
  params: URLSearchParams,
  key: string
): APSliderRangeFilter | undefined {
  const param = params.get(key);
  return !param
    ? undefined
    : SearchParamToAPSliderRangeFilter(param) ?? undefined;
}

export function getStringFilterFromParam(
  params: URLSearchParams,
  key: string
): string | undefined {
  const param = params.get(key);
  return !param ? undefined : SearchParamToStringFilter(param) ?? undefined;
}
